import type { ComponentProps } from 'react';
import React from 'react';

import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import { useLocale } from 'src/app/shared/hooks/l10n';
import { useQueryParamValue } from 'src/app/shared/hooks/location';
import { usePaypal } from 'src/app/shared/hooks/payments';
import type { WithTokenParams } from 'src/app/shared/types';
import { isLoaded, isLoading } from 'src/app/shared/utils/async';
import type { PaymentMethodSaveOptions, PaymentType } from 'src/app/shared/utils/payments';

import { PaypalButtonLoading } from '../PaypalButtonLoading';
import { PaypalButtonSDK } from '../PaypalButtonSDK';
import { PaypalButtonSDKNative } from '../PaypalButtonSDK/PaypalButtonSDKNative';

import { useAvailability } from './hooks/useAvailability';
import { useLoadTimeTracking } from './hooks/useLoadTimeTracking';
import { usePaypalSDK } from './hooks/usePaypalSDK';
import { createBillingAgreement, createOrder, onApprove, onClick, onError } from './utils';

type Props = {
	amount?: number;
	beforeToken: (paymentType: PaymentType) => Promise<boolean>;
	billingAgreementDescription?: string;
	currency: string;
	disabled: boolean;
	email: string;
	firstName?: string;
	lastName?: string;
	flow: paypal.FlowType;
	loading: boolean;
	onTokenError: (paymentType: PaymentType, err: Error) => void;
	onTokenInvalid: (paymentType: PaymentType, err: Error) => void;
	paymentMethodSaveOptions: PaymentMethodSaveOptions;
	style?: ComponentProps<typeof PaypalButtonSDK>['style'];
	validate?: (paymentType: PaymentType) => Promise<boolean>;
	withToken: (params: WithTokenParams) => Promise<void>;
	shouldEnableButton?: boolean;
	trackLoadStartTime: () => void;
	trackLoadEndTime: (duration: number) => void;
	prePaymentChecks?: (paymentType: PaymentType) => Promise<boolean>;
	savePaypalTemporarily?: boolean;
};

// eslint-disable-next-line max-statements
export function PaypalButton(props: Props): React.JSX.Element {
	const utilityContext = useUtilityContext();
	const locale = useLocale();
	const paypalState = usePaypal();
	const ppNative = useQueryParamValue('pp_native');

	const {
		beforeToken,
		currency,
		disabled,
		email,
		flow,
		loading,
		onTokenError,
		onTokenInvalid,
		paymentMethodSaveOptions,
		validate,
		style,
		withToken,
		shouldEnableButton,
		trackLoadEndTime,
		trackLoadStartTime,
		prePaymentChecks,
		savePaypalTemporarily,
		firstName,
		lastName,
	} = props;
	const trackEndTime = useLoadTimeTracking({ flow, trackLoadStartTime, trackLoadEndTime });
	const initializedSdk = usePaypalSDK(currency, flow);

	const setAvailability = useAvailability(shouldEnableButton);

	const paypalButtonStyle: ComponentProps<typeof PaypalButtonSDK>['style'] = {
		shape: 'rect',
		color: 'white',
		layout: 'horizontal',
		label: 'pay',
		...style,
	};

	if (isLoading(paypalState) || !initializedSdk) return <PaypalButtonLoading style={style} />;

	const paypalCheckout = isLoaded(paypalState) ? paypalState.paypalCheckout : null;

	const paypalButtonProps: ComponentProps<typeof PaypalButtonSDK> = {
		fundingSource: 'paypal',
		style: paypalButtonStyle,
		loading,
		onApprove: async (onApproveData) =>
			onApprove(
				{
					firstName,
					lastName,
					email,
					onApproveData,
					onTokenError,
					paypalCheckout,
					paymentMethodSaveOptions,
					withToken,
					prePaymentChecks,
					savePaypalTemporarily,
				},
				utilityContext,
			),

		onCancel: () => onTokenInvalid('paypal', new Error('User cancelled paypal flow')),
		onError: (error) => onError({ onTokenError, error }),
		onInit: (...onInitProps) => {
			setAvailability(...onInitProps);
			trackEndTime();
		},
		onClick: async () => onClick({ beforeToken, validate }),
	};

	if (flow === 'checkout' && initializedSdk === 'checkout') {
		const { amount } = props;
		paypalButtonProps.createOrder = async () =>
			createOrder({
				amount,
				currency,
				disabled,
				paypalCheckout,
			});
	}

	if (flow === 'vault' && initializedSdk === 'vault') {
		const { billingAgreementDescription } = props;
		paypalButtonProps.createBillingAgreement = async () =>
			createBillingAgreement({
				billingAgreementDescription,
				currency,
				disabled,
				locale,
				paypalCheckout,
			});
	}

	if (ppNative === 'true') {
		return <PaypalButtonSDKNative {...paypalButtonProps} />;
	}

	return <PaypalButtonSDK {...paypalButtonProps} />;
}
